import { ChevronRight } from 'lucide-react';

import {
  KIND_ICON,
  KIND_LABEL,
  SIGNAL_LABEL,
  SIGNAL_TONE,
  STATUS_LABEL,
  STATUS_TONE,
  formatWhen,
  hasSignal,
} from './workVocabulary';

import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/cn';
import type { StudentWork, WorkItem } from '@/lib/api/trainer';

/**
 * Everything one student has been set and done on this course, in one list.
 *
 * Replaces a tab that showed assignments alone. Before this the trainer could
 * see assignments here, MCQ quizzes three levels down in the curriculum editor,
 * hackathons as a team scoreboard, and practice coding problems nowhere at all
 * — which happens to be the work type the college is most worried about.
 *
 * Ordered by most recent activity, not by curriculum position, because the
 * question this answers is "what has this student been doing" rather than "what
 * does the syllabus contain".
 *
 * Each row is a doorway to the other half of the picture: the same piece of
 * work with the whole batch against it, which is where "did they do it
 * themselves" stops being a question about one person.
 */

export function StudentWorkTab({
  work,
  onOpenItem,
  openLabel = 'see how everyone did',
  showSelf = true,
}: {
  work: StudentWork;
  /**
   * Opens the work item. Optional because the feed is also rendered where there
   * is nowhere to navigate to — without it the rows are simply not interactive,
   * rather than being dead links.
   */
  onOpenItem?: (item: WorkItem) => void;
  /**
   * What opening a row leads to, for screen readers. Callers differ: from a
   * queue a row opens the whole batch against this work; from a student's own
   * page it opens what that student wrote.
   */
  openLabel?: string;
  /**
   * Whether to list practice the course never set. Off where a row's only
   * destination is the whole-batch view, which self-directed practice has no
   * business having — nobody else was asked to do it.
   */
  showSelf?: boolean;
}) {
  if (work.items.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">Nothing has been set on this course yet.</p>
    );
  }

  const { counts, self_counts: selfCounts } = work;
  const courseItems = work.items.filter((i) => i.source !== 'self');
  const selfItems = work.items.filter((i) => i.source === 'self');

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span>
          <span className="font-semibold text-foreground">{counts.done}</span> of {counts.total}{' '}
          done
        </span>
        {counts.submitted > 0 && <span>{counts.submitted} awaiting grading</span>}
        {counts.not_started > 0 && <span>{counts.not_started} not started</span>}
        {/*
          Only shown when there is something to show. "0 to ask about" every
          time a trainer opens a student is how a signal teaches people to stop
          reading it.
        */}
        {counts.needs_asking > 0 && (
          <span className="font-semibold text-sky-700">{counts.needs_asking} to ask about</span>
        )}
      </div>

      <ul className="space-y-1.5">
        {courseItems.map((item) => (
          <WorkRow
            key={`${item.kind}-${item.id}`}
            item={item}
            onOpen={onOpenItem}
            openLabel={openLabel}
          />
        ))}
      </ul>

      {/*
        Practice nobody set them. Kept below the course and counted separately:
        it is the same student and the same evidence, but answering a different
        question — not "are they keeping up" but "what do they do unprompted".
        A single merged total would answer neither.
      */}
      {showSelf && selfItems.length > 0 && (
        <section className="space-y-2 border-t pt-4">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h4 className="text-sm font-semibold">Practice on their own</h4>
            <span className="text-xs text-muted-foreground">
              <span className="font-semibold text-foreground">{selfCounts.total}</span> problem
              {selfCounts.total === 1 ? '' : 's'} this course never set
              {selfCounts.done > 0 && <> · {selfCounts.done} solved</>}
            </span>
          </div>
          {/*
            Capped rather than paged: a student with three hundred of these
            should not push the rest of their record off the screen, and the
            answer to "show me everything" is a scrollbar, not a "load more".
          */}
          <ul className="max-h-96 space-y-1.5 overflow-y-auto pr-1">
            {selfItems.map((item) => (
              <WorkRow
                key={item.id}
                item={item}
                onOpen={onOpenItem}
                openLabel={openLabel}
              />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function WorkRow({
  item,
  onOpen,
  openLabel,
}: {
  item: WorkItem;
  onOpen?: (item: WorkItem) => void;
  openLabel: string;
}) {
  const Icon = KIND_ICON[item.kind];
  const signal = item.integrity?.signal;
  const showSignal = hasSignal(item.integrity);

  return (
    <li>
      <Card className="flex items-start gap-2.5 px-3 py-2.5 shadow-none">
        <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center text-muted-foreground/70">
          <Icon className="h-4 w-4" aria-hidden="true" />
          <span className="sr-only">{KIND_LABEL[item.kind]}</span>
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            {/*
            The title is the control, not the whole row: the row already
            contains a link to the submission, and an anchor nested inside a
            button is invalid markup that keyboard users pay for.
          */}
            {onOpen ? (
              <button
                type="button"
                onClick={() => onOpen(item)}
                className="group flex min-w-0 items-center gap-1 text-left text-sm font-medium hover:underline"
                aria-label={`${item.title} — ${openLabel}`}
              >
                <span className="truncate">{item.title}</span>
                <ChevronRight
                  className="h-3.5 w-3.5 flex-none text-muted-foreground/50 group-hover:text-foreground"
                  aria-hidden="true"
                />
              </button>
            ) : (
              <span className="truncate text-sm font-medium">{item.title}</span>
            )}
            <Badge
              variant="outline"
              className={cn('flex-none py-px text-[10.5px]', STATUS_TONE[item.status])}
            >
              {STATUS_LABEL[item.status]}
            </Badge>
          </div>

          {/*
            The kind always shows, with or without a topic behind it — practice
            a student found for themselves has no place in the curriculum to
            name, and a row with no second line reads as missing data.
          */}
          <p className="truncate text-xs text-muted-foreground">
            {KIND_LABEL[item.kind]}
            {item.topic_title && <> · {item.topic_title}</>}
          </p>

          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
            {/*
            A score of zero must render, so this tests for presence rather than
            truthiness — `item.score && ...` would silently hide it.
          */}
            {item.score != null && item.max_score != null && (
              <span className="font-semibold tabular-nums text-foreground">
                {item.score}/{item.max_score}
              </span>
            )}
            {item.kind === 'practice' && (item.attempts ?? 0) > 0 && (
              <span className="tabular-nums">
                {item.attempts} attempt{item.attempts === 1 ? '' : 's'}
              </span>
            )}
            {item.integrity && item.integrity.active_mins > 0 && (
              <span className="tabular-nums">{item.integrity.active_mins}m spent</span>
            )}
            {item.last_activity_at && <span>{formatWhen(item.last_activity_at)}</span>}
            {item.submission_url && (
              <a
                href={item.submission_url}
                target="_blank"
                rel="noreferrer noopener"
                className="font-medium text-primary hover:underline"
              >
                Open submission
              </a>
            )}
          </div>

          {showSignal && signal && (
            <p className={cn('mt-1 text-[11px] font-semibold', SIGNAL_TONE[signal])}>
              {SIGNAL_LABEL[signal]}
              {signal !== 'self_solved' && item.integrity && (
                <span className="font-normal text-muted-foreground">
                  {' '}
                  — {Math.round(item.integrity.largest_paste_ratio * 100)}% of the final code
                  arrived in one paste
                </span>
              )}
            </p>
          )}
        </div>
      </Card>
    </li>
  );
}
